// app/javascript/src/containers/Scheduling/EventTypes.tsx
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Helmet } from 'react-helmet'
import copy from 'copy-to-clipboard'
import TopNavigation from '../../components/Navigation/TopNavigation'
import ScrollToTopOnMount from '../../components/Effects/ScrollToTopOnMount'
import NavigationDetection from '../../components/Effects/NavigationDetection'
import PageContent from '../../components/Page/PageContent'
import PageHeader from '../../components/Page/PageHeader'
import PageLoader from '../../components/Page/PageLoader'
import ResourceTable, { ResourceTableAction } from '../../components/Resource/ResourceTable'
import CardEmptyInfo from '../../components/Card/CardEmptyInfo'
import Button from '../../components/Button/Button'
import getEventTypeTableRenderer from '../../components/Resource/EventTypeRenderer'
import Notification from '../../utilities/Notification'
import { EventTypesController } from '../../controllers'
import ERoute from '../../ERoute'
import RouteHelper from '../../helpers/RouteHelper'
import SearchHelper from '../../helpers/SearchHelper'
import LocalStorage, { LocalStorageKey } from '../../LocalStorage'
import { showConfirmModal, showEventTypeModal } from '../../store/modals/actions'
import { ModalsActionTypes } from '../../store/modals/types'
import { AppState } from '../../store'
import { EventType, EventTypeFilter, EventTypeFilters, ResourceListFilterType } from '../../types'

const firstError = (res: any): string | undefined => res?.errors?.[0]?.message

export default function EventTypes() {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch() as (action: ModalsActionTypes) => void
  const setting = useSelector((state: AppState) => state.authentication.currentUser.workspace.setting)

  const initialSearch = React.useMemo(() => SearchHelper.getSearch(location.search, EventTypeFilters), [])
  const [eventTypes, setEventTypes] = React.useState<EventType[]>([])
  const [page, setPage] = React.useState(initialSearch.page)
  const [perPage, setPerPage] = React.useState(initialSearch.perPage)
  const [totalPages, setTotalPages] = React.useState(0)
  const [didInitialLoad, setDidInitialLoad] = React.useState(false)
  const [isFetching, setIsFetching] = React.useState(false)
  const [sortValue, setSortValue] = React.useState<string>(LocalStorage.get(LocalStorageKey.EVENT_TYPE_SORT_VALUE, '-'))
  const [activeFilters, setActiveFilters] = React.useState<any>(initialSearch.filters)
  const [search, setSearch] = React.useState(initialSearch.search)
  const [selectedEventTypeIds, setSelectedEventTypeIds] = React.useState<string[]>([])
  const [, forceUpdate] = React.useReducer(x => x + 1, 0)

  const fetchEventTypes = async (params = { page, perPage, search, activeFilters, sortValue }) => {
    setIsFetching(true)

    try {
      const response = await EventTypesController.getEventTypes({
        page: params.page,
        per_page: params.perPage,
        search: params.search,
        order: params.sortValue,
        ...params.activeFilters,
      })

      if (Array.isArray(response.event_types)) {
        setEventTypes(response.event_types)
        setPage(response.current_page)
        setTotalPages(response.total_pages)
      } else {
        Notification.notifyError(t('EventTypes::Could not load event types'))
      }
    } catch (ex) {
      console.error(ex)
      Notification.notifyError(t('EventTypes::Could not load event types'))
    } finally {
      setIsFetching(false)
      setDidInitialLoad(true)
    }
  }

  const refetch = () => fetchEventTypes()

  const onRouteChange = () => {
    const { search, page, perPage, filters } = SearchHelper.getSearch(window.location.search, EventTypeFilters)

    setSearch(search)
    setPage(page)
    setPerPage(perPage)
    setActiveFilters(filters)
    fetchEventTypes({ page, perPage, search, activeFilters: filters, sortValue })
  }

  const replaceRoute = (params: { page: number, search: string, activeFilters: any, sortValue: string, perPage?: number }) => {
    RouteHelper.replace(location.pathname as ERoute, {
      page: params.page,
      search: params.search,
      ...params.activeFilters,
      sortValue: params.sortValue,
      ...(params.perPage ? { perPage: params.perPage } : {}),
    })
  }

  const openEditor = (eventType: EventType) => {
    history.push(RouteHelper.process(ERoute.PATH_SCHEDULING_EVENT_TYPE, { id: eventType.id }))
  }

  const onNewEventTypeClick = (e?: React.MouseEvent) => {
    if (e) e.preventDefault()

    requestAnimationFrame(() => {
      dispatch(showEventTypeModal({
        onSubmit: (eventType) => openEditor(eventType),
      }))
    })
  }

  const onTableActionClick = (key: string, eventType: EventType) => {
    switch (key) {
      case 'preview': onPreviewClick(eventType)
        break
      case 'copy_link': onCopyLinkClick(eventType)
        break
      case 'toggle_active': onToggleActiveClick(eventType)
        break
      case 'duplicate': onDuplicateClick(eventType)
        break
      case 'edit': onEditClick(eventType)
        break
      case 'delete': onDeleteClick(eventType)
        break
      default:
        throw Error('[EventTypes] Unimplemented onTableActionClick')
    }
  }

  const onTableSelectionChange = (eventTypeIds: string[]) => setSelectedEventTypeIds(eventTypeIds)

  const onTableRowSelectionChange = (selected: boolean, eventTypeId: string) => {
    setSelectedEventTypeIds(ids => selected ? [...ids, eventTypeId] : ids.filter(id => id !== eventTypeId))
  }

  const onPreviewClick = (eventType: EventType) => {
    if (eventType.public_url) window.open(eventType.public_url, '_blank', 'noopener')
  }

  const onCopyLinkClick = (eventType: EventType) => {
    if (!eventType.public_url) return
    copy(eventType.public_url)
    Notification.notifySuccess(t('EventTypes::Link copied'))
  }

  const onToggleActiveClick = async (eventType: EventType) => {
    try {
      const response = await EventTypesController.update({ id: eventType.id, active: !eventType.active })
      if (response.id) {
        setEventTypes(list => list.map(item => item.id === response.id ? response : item))
      } else {
        Notification.notifyError(firstError(response) || t('EventTypes::Could not save'))
      }
    } catch (ex) {
      console.error(ex)
      Notification.notifyError(t('EventTypes::Could not save'))
    }
  }

  const onDuplicateClick = async (eventType: EventType) => {
    try {
      const response = await EventTypesController.duplicate(eventType.id)
      if (response.id) {
        Notification.notifySuccess(t('EventTypes::Event type duplicated'))
        refetch()
      } else {
        Notification.notifyError(t('EventTypes::Could not duplicate event type'))
      }
    } catch (ex) {
      console.error(ex)
      Notification.notifyError(t('EventTypes::Could not duplicate event type'))
    }
  }

  const onEditClick = (eventType: EventType) => {
    requestAnimationFrame(() => {
      dispatch(showEventTypeModal({
        eventType: eventType,
        onSubmit: (updatedEventType) => {
          setEventTypes(list => list.map(e => e.id === updatedEventType.id ? updatedEventType : e))
        },
      }))
    })
  }

  const onDeleteClick = (eventType: EventType) => {
    dispatch(showConfirmModal({
      title: t('EventTypes::Delete event type'),
      description: t('EventTypes::You are about to delete {{title}}. Are you sure?', { title: eventType.title }),
      action: { label: t('EventTypes::Delete'), isDestructive: true },
      onConfirm: async () => {
        try {
          const response = await EventTypesController.delete(eventType.id)
          if (response.error) {
            Notification.notifyError(t('EventTypes::Could not delete event type'))
          } else {
            setEventTypes(list => list.filter(item => item.id !== eventType.id))
            Notification.notifySuccess(t('EventTypes::Event type successfully deleted'))
          }
        } catch (ex) {
          console.error(ex)
          Notification.notifyError(t('EventTypes::Could not delete event type'))
        }
      },
    }))
  }

  const onBulkDeleteClick = () => {
    dispatch(showConfirmModal({
      title: t('EventTypes::Delete event types'),
      description: t('EventTypes::You are about to delete {{count}} event types. Are you sure?', { count: selectedEventTypeIds.length }),
      action: { label: t('EventTypes::Delete'), isDestructive: true },
      onConfirm: async () => {
        try {
          await EventTypesController.bulkDelete(selectedEventTypeIds)

          setEventTypes(list => list.filter(eventType => !selectedEventTypeIds.includes(eventType.id)))
          setSelectedEventTypeIds([])
        } catch (ex) {
          console.error(ex)
        }
      },
    }))
  }

  const onFiltersChange = (filters: any) => {
    setPage(1)
    setActiveFilters(filters)
    replaceRoute({ page: 1, search, activeFilters: filters, sortValue })
  }

  const onSortValueChange = (value: string) => {
    LocalStorage.set(LocalStorageKey.EVENT_TYPE_SORT_VALUE, value)
    setPage(1)
    setSortValue(value)
    replaceRoute({ page: 1, search, activeFilters, sortValue: value })
  }

  const onSearchSubmit = (value: string) => {
    setPage(1)
    setSearch(value)
    replaceRoute({ page: 1, search: value, activeFilters, sortValue })
  }

  const onClearFilters = () => {
    setPage(1)
    setSearch('')
    setActiveFilters({})
    replaceRoute({ page: 1, search: '', activeFilters: {}, sortValue })
  }

  const onPageChange = (value: number) => {
    setPage(value)
    replaceRoute({ page: value, search, activeFilters, sortValue })
  }

  const onPerPageChange = (value: number) => {
    LocalStorage.set(LocalStorageKey.PER_PAGE, value)
    setPage(1)
    setPerPage(value)
    replaceRoute({ page: 1, search, activeFilters, sortValue, perPage: value })
  }

  const onColumnsChange = (columns: string[]) => {
    LocalStorage.set(LocalStorageKey.EVENT_TYPE_COLUMNS, columns)
    forceUpdate()
  }

  const activeFiltersCount = search?.length > 0 || Object.keys(activeFilters).length > 0
  const promotedBulkActions: ResourceTableAction[] = [
    { icon: 'trash-alt-solid', content: t('EventTypes::Delete'), onAction: onBulkDeleteClick, destructive: true }
  ]

  const filters: EventTypeFilter[] = [
    { name: 'title', label: t('EventType::Title'), type: ResourceListFilterType.STRING },
    { name: 'user_id', label: t('EventType::host'), type: ResourceListFilterType.SINGLE_RESOURCE, resourceType: 'user', isValidNewOption: () => false },
    { name: 'active', label: t('EventType::Active'), type: ResourceListFilterType.SINGLE_OPTION, options: [{ label: t('EventTypes::Yes'), value: String(true) }, { label: t('EventTypes::No'), value: String(false) }] },
    { name: 'created_at', label: t('EventType::Created date'), type: ResourceListFilterType.DATE },
  ]

  const { headers, columns, selectedColumns, renderRow } = getEventTypeTableRenderer({
    setting: setting,
    onRowClick: openEditor,
    selectedItems: selectedEventTypeIds,
    onSelectionChange: onTableRowSelectionChange,
    onActionClick: onTableActionClick,
  })

  return (
    <>
      <Helmet>
        <title>{t('EventTypes::{{__appName}} | Scheduling')}</title>
      </Helmet>
      <ScrollToTopOnMount />
      <NavigationDetection onRouteChange={onRouteChange} />

      <TopNavigation
        icon='calendar-clock'
        title={t('EventTypes::Event types')}
        action={
          <Button
            type='primary'
            icon='plus'
            text={t('EventTypes::New event type')}
            onClick={onNewEventTypeClick}
            extraClasses='page-action'
          />
        }
      />

      <PageContent>
        <PageHeader title={t('EventTypes::Event types')} />

        {!didInitialLoad && <PageLoader />}
        {didInitialLoad && <ResourceTable
          data={eventTypes}
          headers={headers}
          renderRow={renderRow}
          renderEmpty={<CardEmptyInfo
            icon={activeFiltersCount ? 'search' : 'calendar-clock'}
            description={activeFiltersCount ? t('EventTypes::No event types found') : t('EventTypes::Event types let people book time with you through a shareable link.')}
            descriptionActionText={activeFiltersCount ? t('EventTypes::Clear filters') : t('EventTypes::Create your first event type')}
            onDescriptionActionClick={activeFiltersCount ? onClearFilters : onNewEventTypeClick}
          />}
          filters={filters}
          activeFilters={activeFilters}
          onFiltersChange={onFiltersChange}
          sortOptions={[
            { label: '-', value: '-' },
            { label: t('EventType::Title (A-Z)'), value: 'title_asc' },
            { label: t('EventType::Title (Z-A)'), value: 'title_desc' },
            { label: t('EventType::Duration ↑'), value: 'duration_asc' },
            { label: t('EventType::Duration ↓'), value: 'duration_desc' },
            { label: t('EventType::Created at ↑'), value: 'created_at_asc' },
            { label: t('EventType::Created at ↓'), value: 'created_at_desc' },
          ]}
          promotedBulkActions={promotedBulkActions}
          bulkActions={[]}
          sortValue={sortValue}
          onSortChange={onSortValueChange}
          pagination={{ page: page, perPage: perPage, pageCount: totalPages }}
          onPageChange={onPageChange}
          onPerPageChange={onPerPageChange}
          isLoading={isFetching}
          stickyHeader={true}
          selectedItems={selectedEventTypeIds}
          onSelectionChange={onTableSelectionChange}
          searchValue={search}
          onSearchChange={setSearch}
          onSearchSubmit={onSearchSubmit}
          columns={columns}
          selectedColumns={selectedColumns}
          onColumnsChange={onColumnsChange}
          maxHeight='60vh'
        />}
      </PageContent>
    </>
  )
}
